<template>
  <div class="flex flex-col relative h-full border-top">
    <div
      ref="surveyPage"
      class="flex-1 overflow-y-auto"
      :style="{
        'padding-top': setyleNavHeight + 'px'
      }"
    >
      <div id="screenshotUserInfo"></div>
      <template v-for="(content, index) in contents">
        <component
          :is="component(content)"
          :key="index"
          :data="content"
          :index="index"
          :isSubmit="isSubmit"
          :userAnswer="getUserAnswer(index, submitData.data)"
          class="md:ml-18 p-6"
          @addField="handleAddField"
          @fieldChange="handleCheckField"
        ></component>
      </template>
    </div>
    <div class="h-16 py-2 flex items-center justify-center bg-white">
      <div class="fixed w-full bg-motif-100 bottom-16">
        <div class="h-1 bg-motif" :style="progress"></div>
      </div>
      <template v-if="isSubmit || !!portalView">
        <div class="button mr-4 back-button" @click="submit">返回</div>
        <div class="button button-color text-white" @click.stop="handleScreenshot">保存并分享</div>
      </template>
      <div
        v-else
        :class="[
          'button',
          submitDisabled ? 'bg-gray-200 text-gray-400' : 'button-color text-white'
        ]"
        @click="submit"
      >
        提交
      </div>
    </div>
    <PreviewScreemshot
      :url.sync="screenshotUrl"
      :visible.sync="screenshotModelVisible"
      :title="sequence.title"
    />
    <!-- 重新学习按钮组 -->
    <div class="multifunction-slot">
      <slot></slot>
    </div>
  </div>
</template>
<script lang="ts">
import { Vue, Component, Prop, Mixins } from 'vue-property-decorator';
import { Content, Sequence } from '@forchange/storytime';
import ScreenshotUserInfo from './components/UserInfo.vue';
import PreviewScreemshot from './components/PreviewScreemshot.vue';
// resources
import { Choice, ShortAnswer } from '@/components/resources';
// models
import Dispatcher from '@/pages/main/models/Dispatcher';
import { baseSurvey, SurveySaveData } from '@/components/resources/survey/baseSurvey';
import { namespace } from 'vuex-class';
import { SurveyRecord, UserInfo } from '@/store/modules/common/types';
import { Environment } from 'prismjs';
import Html2canvas from 'html2canvas';
import { ReviewData } from '@/store/modules/survey/types';
import ScrollListenMixin from '@/mixins/scrollListen';

const userModule = namespace('common');

const dispatcher = new Dispatcher({
  Radio: Choice,
  Checkbox: Choice,
  Text: ShortAnswer
});

export type SurveyData = {
  $id: string;
  $kind: string;
  options: Array<optionContent>;
  title: string;
};

export type optionContent = {
  content: string;
  mark: string;
};

// 需要等待用户输入的组件
@Component({
  components: {
    ScreenshotUserInfo,
    PreviewScreemshot
  }
})
export default class Survey extends Mixins(ScrollListenMixin) {
  @Prop({ type: Object }) sequence!: Sequence & {
    title: string;
  };
  @userModule.State('surveyRecord') surveyRecord: SurveyRecord;
  @userModule.State('portalSequence') portalSequence: Array<Sequence>;
  @userModule.State('authingId') authingId: string;
  @userModule.State('version') version: string;
  @userModule.State('environment') environment: Environment;
  @userModule.State('userInfo') userInfo: UserInfo;
  @userModule.State('courseName') courseName: string;

  fields: Array<Vue & baseSurvey> = [];

  submitDisabled = true;
  submitData: Array<SurveySaveData> = [];
  isSubmit = false;

  initializing = false;

  screenshotUrl = '';
  screenshotModelVisible = false;

  get contents() {
    // 受portal影响，不要使用window.storyteller.topContet取
    return (this.sequence.contents[0] as Content & { value: Array<SurveyData> })?.value || [];
  }

  get portalView() {
    return this.portalSequence[this.portalSequence.length - 1];
  }

  get contentId() {
    return this.sequence.contents[0].$id.split('#')[0];
  }

  get occurredContentSize() {
    return window.storyteller.occurredContentIds.size;
  }

  get totalContentSize() {
    return window.storyteller.totalContentIds.size || 1;
  }

  get progress(): Object {
    if (window.storyteller.topContet?.$kind === 'End') {
      return {
        width: '100%'
      };
    }
    return {
      width: `${(this.occurredContentSize / this.totalContentSize) * 100}%`
    };
  }

  mounted() {
    this.initScrollListen(this.$refs.surveyPage as HTMLElement);
    this.initSubmitData();
    // 使用传送门的情况
    const topContent = this.sequence.contents[0];
    // 标记位 避免记录恢复异常
    window.storyteller.updateAssignContent({
      $id: topContent.$id,
      $kind: topContent.$kind,
      ...{
        forceTrigger: true
      }
    });
  }

  async initSubmitData() {
    this.initializing = true;

    const surveyDataStr = (await this.$store.dispatch('survey/getReviewData', {
      contentId: this.contentId
    })) as Array<ReviewData> | undefined;
    if (!surveyDataStr || typeof surveyDataStr[0]?.content !== 'string') {
      this.initializing = false;
      return;
    }

    const surveyData = JSON.parse(surveyDataStr[0].content) as {
      submit: boolean;
      data: Array<SurveySaveData>;
    };

    this.submitData = surveyData.data;
    this.isSubmit = surveyData.submit;
    this.updateFieldAuswer(this.submitData);

    this.initializing = false;
  }

  updateFieldAuswer(submitData: Array<SurveySaveData>) {
    this.$nextTick(() => {
      this.fields.forEach((field) => {
        field.updateUserAnswer(submitData[field.index]);
      });
    });
  }

  getUserAnswer(index: number, submitDataList: Array<{ index: number }> | undefined) {
    const targetArray = submitDataList?.filter((item) => item.index === index);
    if (targetArray?.length) {
      return targetArray[0];
    }
    return;
  }

  private component(content: Content) {
    return dispatcher.dispatch(content);
  }

  handleAddField(field: Array<Vue & baseSurvey>) {
    if (field) {
      this.fields.push(...field);
    }
  }

  async submit() {
    if (this.portalView) {
      const portal = this.portalSequence[this.portalSequence.length - 1] as Sequence & {
        scrollTop: number;
      };
      if (portal) {
        this.$store.commit('common/setCallStatus', {
          isroll: false,
          rollHeight: portal.scrollTop
        });
      }
      this.$store.commit('common/setLoading', true);
      this.$store.commit('common/popPortal');
      return;
    }
    // 目录情况也会有填过再进入情况
    if (this.isSubmit) return window.next();
    const emptyFields = this.checkFields();
    if (emptyFields.length) {
      this.$Message.warn({
        time: 3000,
        content: '请对未作答或不符合要求题目进行调整'
      });
      (emptyFields[0] as Vue & { followField: Function }).followField();
      return;
    }
    await this.submitCommit(true);
    this.$Message.success({
      time: 1500,
      content: '提交成功啦！'
    });
    this.isSubmit = true;
  }

  async submitCommit(submitStatus: boolean) {
    if (this.portalView || this.isSubmit || this.initializing) return;
    const submitData = this.fields.map((item) => {
      return item.getValue();
    });

    this.submitData = submitData.sort(
      (beforeValue, afterValue) => beforeValue.index - afterValue.index
    );

    const submitDataJson = JSON.stringify({
      submit: submitStatus,
      data: this.submitData
    });

    this.$store.dispatch('survey/setData', {
      contentId: this.contentId,
      content: submitDataJson
    });
  }

  saveStore(submit: boolean) {
    this.$store.commit('common/setSurveyRecord', {
      contentId: this.contentId,
      content: this.submitData,
      submit
    });
  }

  checkFields() {
    return this.fields.filter((item) => {
      return (item as Vue & { checkEmpty: Function }).checkEmpty();
    });
  }

  handleCheckField() {
    this.$nextTick(() => {
      const emptyFields = this.checkFields();
      this.submitDisabled = !!emptyFields.length;
      this.submitCommit(false);
    });
  }

  async handleScreenshot() {
    this.screenshotModelVisible = true;
    setTimeout(async () => {
      const canvasSourceEl = this.cloneElement();
      if (!canvasSourceEl.cloneEl) {
        this.screenshotModelVisible = false;
        return;
      }
      await this.buildElement(canvasSourceEl.cloneEl);
      const options = {
        width: Math.min(canvasSourceEl.cloneEl.offsetWidth, 768),
        useCORS: true
      };
      const canvasEl = await Html2canvas(canvasSourceEl.cloneEl, options);
      this.screenshotUrl = canvasEl.toDataURL('image/png');
      canvasSourceEl.removeHook();
    }, 0);
  }

  cloneElement() {
    const el = (this.$refs.surveyPage as HTMLDivElement).cloneNode(true);
    const container = document.createElement('div');
    container.appendChild(el);
    container.setAttribute('id', 'canvas-view');
    document.body.appendChild(container);
    const cloneEl = document.body.querySelector('#canvas-view') as HTMLDivElement;
    return {
      cloneEl,
      removeHook: () => {
        document.body.removeChild(container);
      }
    };
  }

  async buildElement(el: HTMLDivElement) {
    let url = '';
    if (this.userInfo.avator) {
      // 图片本地化
      try {
        url = await new Promise((resolve, reject) => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = document.createElement('img');
          img.setAttribute('crossorigin', 'anonymous');

          img.src = this.userInfo.avator;
          img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx?.drawImage(img, 0, 0);
            resolve(canvas.toDataURL());
          };
          img.onerror = (e) => {
            reject(e);
          };
        });
      } catch (error) {
        console.log('error: ', error);
      }
    }

    // 添加头部用户信息组件
    const replaceEl = el.querySelector('#screenshotUserInfo');
    if (!replaceEl) return;
    const screenshotUserInfo = new ScreenshotUserInfo({
      propsData: {
        title: this.sequence.title || this.courseName,
        avator: url,
        nickname: this.getNick()
      }
    }).$mount();
    replaceEl.parentElement?.replaceChild(screenshotUserInfo.$el, replaceEl);

    // 规避html2canvas textarea渲染问题
    const textareaEls = el.querySelectorAll('.textarea');
    textareaEls.forEach((textareaEl) => {
      const divEl = document.createElement('div');
      divEl.setAttribute('style', 'padding: 1rem; white-space: pre-line; line-height: 1.75rem;');
      divEl.innerHTML = (textareaEl as HTMLTextAreaElement).value;
      textareaEl.parentElement?.replaceChild(divEl, textareaEl);
    });

    // 兼容ISO15.0字体缺失渲染空白
    const styleArray = [];
    navigator.userAgent.match(/cpu iphone os (.*) like mac os x/i);
    // eslint-disable-next-line radix
    const version = parseInt(RegExp.$1);
    if (version >= 15.0) {
      styleArray.push('font-family: PingFangSC-Regular;');
    }

    // 添加背景
    styleArray.push(
      'max-width: 768px;background-image: linear-gradient(15deg, #fff 85%, #f3f1fd);background-repeat: no-repeat;background-position: right 0;'
    );
    el.setAttribute('style', styleArray.join(''));
  }

  getNick() {
    return (
      this.userInfo.nickname ||
      `学员${(this.userInfo.phone &&
        this.userInfo.phone.substring(this.userInfo.phone.length - 4)) ||
        (this.authingId && this.authingId.substring(this.authingId.length - 4))}`
    );
  }
}
</script>
<style scoped>
.button {
  @apply w-48 h-10 rounded-md flex items-center justify-center cursor-pointer;
}

.back-button {
  @apply text-motif-500 border-motif-500;
  border: 1px solid;
}

.back-button:hover {
  @apply bg-motif-100;
}

.button-color {
  @apply bg-motif hover:opacity-hover active:opacity-active;
}

/* slot position */
.multifunction-slot {
  @apply fixed right-4 bottom-20;
}
</style>
